import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  WarehouseStockTemplate,
  loadWarehouseStockTemplates,
  saveWarehouseStockTemplate,
  deleteWarehouseStockTemplate,
} from '../utils/warehouseStockTemplates';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Checkbox,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  Chip,
  useMediaQuery,
  useTheme,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useTranslation } from 'react-i18next';

// Tipler
export interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

export interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

export interface InventoryItem {
  id: string;
  name: string;
  categoryId?: string | null;
  warehouseId?: string | null;
  salePrice?: number;
  stockCode?: string;
  barcode?: string;
  imageUrl?: string; // Ürün resmi URL'si
}

interface WarehouseStockSettingsTabProps {
  allWarehouses: Warehouse[];
  allCategories: InventoryCategory[];
  // ⚠️ allInventoryItems artık kullanılmıyor - ürünler modal içinde lazy load ediliyor
  // Mevcut UI'da ürün adlarını göstermek için şimdilik boş array gönderilecek
  selectedWarehouseIds: string[];
  selectedCategoriesPerWarehouse: { [warehouseId: string]: string[] };
  selectedProductsPerCategory: { [warehouseId: string]: { [categoryId: string]: string[] } };
  selectedProductsDetails: { [productId: string]: any }; // ✅ YENİ: Ürün detayları
  activeWarehouseSubTab: number;
  activeCategorySubTabPerWarehouse: { [warehouseId: string]: number };
  selectedProductsPage: { [key: string]: number };
  selectedProductsRowsPerPage: { [key: string]: number };
  preventNegativeStock: boolean;
  showProductImages: boolean;
  allowAccountCreationOnSale: boolean;
  
  // Event handlers
  onWarehouseSelectionModalOpen: () => void;
  onActiveWarehouseSubTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onRemoveWarehouse: (event: React.MouseEvent, warehouseIdToRemove: string) => void;
  onRemoveCategory: (event: React.MouseEvent, warehouseId: string, categoryIdToRemove: string) => void;
  onRemoveProduct?: (warehouseId: string, categoryId: string, productIdToRemove: string) => void; // ✅ YENİ
  onMoveCategory?: (warehouseId: string, categoryId: string, direction: 'left' | 'right') => void; // ✅ YENİ: Kategori sıralama
  onActiveCategorySubTabChange: (warehouseId: string, newValue: number) => void;
  onOpenCategoryModal: (warehouseId: string) => void;
  onOpenInventoryCategoryModal: () => void;
  onOpenProductModal: (warehouseId: string, categoryId: string) => void;
  onSelectedProductsPageChange: (key: string, newPage: number) => void;
  onSelectedProductsRowsPerPageChange: (key: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPreventNegativeStockChange: (checked: boolean) => void;
  onShowProductImagesChange: (checked: boolean) => void;
  onUploadProductImage?: (productId: string, companyId: string) => void;
  onAllowAccountCreationOnSaleChange: (checked: boolean) => void;

  // ✅ YENİ: Şube paket fiyatı kullan toggle'ı.
  // Açıkken ürünler InventoryBranchPrice.PackagePrice ile gösterilir;
  // paket fiyatı yok/0 ise şube satış fiyatına (SalePrice) düşer.
  usePackagePrices?: boolean;
  onUsePackagePricesChange?: (checked: boolean) => void;
  // Ekrana bir şube bağlıysa switch aktif olur, yoksa disabled + tooltip.
  hasBranchAssigned?: boolean;

  // ✅ YENİ: Depo/Stok taslak sistemi (şirket genel, isimli taslaklar).
  // Taslaklar localStorage'da tutulur; yalnızca depo/kategori/ürün
  // seçimlerini içerir. Diğer tab ayarlarını etkilemez.
  companyId?: string | null;
  onApplyWarehouseTemplate?: (template: WarehouseStockTemplate) => void;
}

const WarehouseStockSettingsTab: React.FC<WarehouseStockSettingsTabProps> = ({
  allWarehouses,
  allCategories,
  selectedWarehouseIds,
  selectedCategoriesPerWarehouse,
  selectedProductsPerCategory,
  selectedProductsDetails, // ✅ YENİ
  activeWarehouseSubTab,
  activeCategorySubTabPerWarehouse,
  selectedProductsPage,
  selectedProductsRowsPerPage,
  preventNegativeStock,
  showProductImages,
  allowAccountCreationOnSale,
  onWarehouseSelectionModalOpen,
  onActiveWarehouseSubTabChange,
  onRemoveWarehouse,
  onRemoveCategory,
  onRemoveProduct, // ✅ YENİ
  onMoveCategory, // ✅ YENİ: Kategori sıralama
  onActiveCategorySubTabChange,
  onOpenCategoryModal,
  onOpenInventoryCategoryModal,
  onOpenProductModal,
  onSelectedProductsPageChange,
  onSelectedProductsRowsPerPageChange,
  onPreventNegativeStockChange,
  onShowProductImagesChange,
  onUploadProductImage,
  onAllowAccountCreationOnSaleChange,
  usePackagePrices = false,
  onUsePackagePricesChange,
  hasBranchAssigned = false,
  companyId,
  onApplyWarehouseTemplate,
}) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Depo/Stok taslakları (şirket genel, localStorage) ──────────────────
  const [templates, setTemplates] = useState<WarehouseStockTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState('');
  const [saveDialogError, setSaveDialogError] = useState<string | null>(null);

  const templatesEnabled = Boolean(companyId && onApplyWarehouseTemplate);

  const refreshTemplates = useCallback(() => {
    if (!companyId) {
      setTemplates([]);
      return;
    }
    setTemplates(loadWarehouseStockTemplates(companyId));
  }, [companyId]);

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const hasAnySelection = selectedWarehouseIds.length > 0;

  const handleOpenSaveDialog = () => {
    if (!templatesEnabled) return;
    setSaveDialogName(selectedTemplate?.name || '');
    setSaveDialogError(null);
    setSaveDialogOpen(true);
  };

  const handleConfirmSaveTemplate = () => {
    if (!companyId) return;
    const name = saveDialogName.trim();
    if (!name) {
      setSaveDialogError('Taslak adı boş olamaz.');
      return;
    }
    const saved = saveWarehouseStockTemplate(companyId, {
      name,
      selectedWarehouseIds,
      selectedCategoriesPerWarehouse,
      selectedProductsPerCategory,
      // ✅ YENİ: Şube paket fiyatı kullan bayrağı taslağa dahil
      usePackagePrices,
    });
    if (!saved) {
      setSaveDialogError('Taslak kaydedilemedi. LocalStorage dolu olabilir.');
      return;
    }
    refreshTemplates();
    setSelectedTemplateId(saved.id);
    setSaveDialogOpen(false);
  };

  const handleApplySelectedTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId || !onApplyWarehouseTemplate) return;
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    onApplyWarehouseTemplate(tpl);
  };

  const handleDeleteSelectedTemplate = () => {
    if (!companyId || !selectedTemplateId) return;
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    const ok = window.confirm(`"${tpl.name}" taslağı silinsin mi?`);
    if (!ok) return;
    const success = deleteWarehouseStockTemplate(companyId, selectedTemplateId);
    if (success) {
      setSelectedTemplateId('');
      refreshTemplates();
    }
  };
  
  // Seçili depoları al
  const selectedWarehouses = allWarehouses.filter(w => selectedWarehouseIds.includes(w.id));
  const activeWarehouse = selectedWarehouses[activeWarehouseSubTab];

  // Resim yükleme butonu tıklama işleyicisi
  const handleImageButtonClick = (event: React.MouseEvent, productId: string) => {
    event.stopPropagation(); // Diğer tıklama olaylarını engelle
    
    // Eğer onUploadProductImage prop'u tanımlanmışsa çağır
    if (onUploadProductImage) {
      onUploadProductImage(productId, ''); // CompanyId daha sonra güncellenecek
    }
  };

  // ✅ Depo/Stok taslak bandı (mobil + masaüstü ortak). Yalnızca
  // companyId + onApplyWarehouseTemplate verilmişse render edilir.
  // Sadece bu tab'da görünür — diğer tab ayarlarından bağımsız.
  const renderTemplatesBar = () => {
    if (!templatesEnabled) return null;
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 1,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <BookmarkIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
            Depo/Stok Taslakları
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
          <Select
            value={selectedTemplateId}
            displayEmpty
            onChange={(e) => handleApplySelectedTemplate(e.target.value as string)}
          >
            <MenuItem value="">
              <em>{templates.length === 0 ? 'Henüz taslak yok' : 'Taslak Seç (Yükle)'}</em>
            </MenuItem>
            {templates.map(t => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Tooltip title={hasAnySelection ? 'Mevcut depo/kategori/ürün seçimlerini taslak olarak kaydet' : 'Kaydetmek için önce depo/kategori/ürün seçin'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BookmarkAddIcon fontSize="small" />}
                onClick={handleOpenSaveDialog}
                disabled={!hasAnySelection}
              >
                Taslak Olarak Kaydet
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Seçili taslağı sil">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={handleDeleteSelectedTemplate}
                disabled={!selectedTemplateId}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 0 }}>
      {renderTemplatesBar()}
      {isMobile ? (
        // 📱 MOBİL GÖRÜNÜM
        <Box sx={{ p: 1.5, pb: 0 }}>
          {/* Üst Satır: Başlık + Depo Seç Butonu */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">{t('quickSaleSettingsModal.tabs.warehouseStock')}</Typography>
            <Button 
              variant="contained" 
              size="small" 
              onClick={onWarehouseSelectionModalOpen}
              startIcon={<AddIcon fontSize='small' />}
            >
              {t('warehouseStockSettingsTab.selectWarehouse')}
            </Button>
          </Box>
          
          {/* Alt Satır: Switch'ler */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption">{t('warehouseStockSettingsTab.preventNegativeStock')}</Typography>
              <Switch
                checked={preventNegativeStock}
                onChange={(e) => onPreventNegativeStockChange(e.target.checked)}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption">{t('warehouseStockSettingsTab.showProductImages')}</Typography>
              <Switch
                checked={showProductImages}
                onChange={(e) => onShowProductImagesChange(e.target.checked)}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption">{t('warehouseStockSettingsTab.accountEntryOperations')}</Typography>
              <Switch
                checked={allowAccountCreationOnSale}
                onChange={(e) => onAllowAccountCreationOnSaleChange(e.target.checked)}
                size="small"
              />
            </Box>
            <Tooltip
              title={hasBranchAssigned
                ? 'Açıkken ürünlerin şube paket fiyatı gösterilir; paket fiyatı tanımsızsa şube satış fiyatına düşer.'
                : 'Bu ayar şube seçili ekranlarda çalışır.'}
              arrow
              placement="top"
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color={hasBranchAssigned ? 'text.primary' : 'text.disabled'}>
                  Şube paket fiyatı kullan
                </Typography>
                <Switch
                  checked={hasBranchAssigned && !!usePackagePrices}
                  onChange={(e) => onUsePackagePricesChange?.(e.target.checked)}
                  size="small"
                  disabled={!hasBranchAssigned || !onUsePackagePricesChange}
                />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        // 💻 MASAÜSTÜ GÖRÜNÜM
        <>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, pb: 0}}>
            <Typography variant="subtitle1" gutterBottom sx={{mb: 0}}>{t('quickSaleSettingsModal.tabs.warehouseStock')}</Typography>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preventNegativeStock}
                    onChange={(e) => onPreventNegativeStockChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{fontSize: '0.8rem'}}>{t('warehouseStockSettingsTab.preventNegativeStock')}</Typography>}
                sx={{mr: 2, height: '30px'}}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showProductImages}
                    onChange={(e) => onShowProductImagesChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{fontSize: '0.8rem'}}>{t('warehouseStockSettingsTab.showProductImages')}</Typography>}
                sx={{mr: 2, height: '30px'}}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowAccountCreationOnSale}
                    onChange={(e) => onAllowAccountCreationOnSaleChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{fontSize: '0.8rem'}}>{t('warehouseStockSettingsTab.accountEntryOperations')}</Typography>}
                sx={{mr: 2, height: '30px'}}
              />
              <Tooltip
                title={hasBranchAssigned
                  ? 'Açıkken ürünlerin şube paket fiyatı gösterilir; paket fiyatı tanımsızsa şube satış fiyatına düşer.'
                  : 'Bu ayar şube seçili ekranlarda çalışır.'}
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasBranchAssigned && !!usePackagePrices}
                      onChange={(e) => onUsePackagePricesChange?.(e.target.checked)}
                      size="small"
                      disabled={!hasBranchAssigned || !onUsePackagePricesChange}
                    />
                  }
                  label={<Typography variant="body2" sx={{fontSize: '0.8rem', color: hasBranchAssigned ? 'inherit' : 'text.disabled'}}>Şube paket fiyatı kullan</Typography>}
                  sx={{mr: 2, height: '30px'}}
                />
              </Tooltip>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={onWarehouseSelectionModalOpen}
                startIcon={<AddIcon fontSize='small' />}
                sx={{height: '30px'}}
              >
                {t('warehouseStockSettingsTab.selectWarehouse')}
              </Button>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block', px: 1.5}}>
            {t('warehouseStockSettingsTab.description')}
          </Typography>
        </>
      )}
      <Divider sx={{ mb: 0 }}/>
      
      {selectedWarehouses.length === 0 ? (
        <Box sx={{p: 1.5}}>
          <Typography variant="caption" color="text.secondary">
            {t('warehouseStockSettingsTab.selectWarehouseHint')}
          </Typography>
        </Box>
      ) : (
        <Box>
          {isMobile ? (
            // 📱 MOBİL: Depo seçimi dropdown + Kategori butonları alt satırda
            <Box sx={{ p: 1.5, pb: 0 }}>
              {/* Depo Seçimi - Select */}
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel id="warehouse-select-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarehouseIcon fontSize="small" /> {t('warehouseStockSettingsTab.selectedWarehouse')}
                  </Box>
                </InputLabel>
                <Select
                  labelId="warehouse-select-label"
                  value={activeWarehouseSubTab}
                  label={t('warehouseStockSettingsTab.selectedWarehouse')}
                  onChange={(e) => {
                    onActiveWarehouseSubTabChange(e as unknown as React.SyntheticEvent, e.target.value as number);
                  }}
                >
                  {selectedWarehouses.map((warehouse, index) => (
                    <MenuItem key={warehouse.id} value={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography variant="body2">{warehouse.name}</Typography>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveWarehouse(e, warehouse.id);
                          }}
                          sx={{ ml: 1, p: 0.2 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Kategori Butonları */}
              {activeWarehouse && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => onOpenCategoryModal(activeWarehouse.id)}
                    startIcon={<SettingsIcon sx={{ fontSize: '1rem !important' }} />}
                    sx={{ flex: 1, fontSize: '0.7rem' }}
                  >
                    {t('warehouseStockSettingsTab.manageCategoriesShort')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={onOpenInventoryCategoryModal}
                    startIcon={<CategoryIcon sx={{ fontSize: '1rem !important' }} />}
                    sx={{ flex: 1, fontSize: '0.7rem' }}
                  >
                    {t('warehouseStockSettingsTab.newCategoryShort')}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            // 💻 MASAÜSTÜ: Tab görünümü
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pl: 1.5, pr: 1.5, pt:0.5, pb:0.5 }}>
              <Tabs
                value={activeWarehouseSubTab}
                onChange={onActiveWarehouseSubTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label={t('warehouseStockSettingsTab.selectedWarehouseTabsAria')}
                sx={{ flexGrow: 1, minHeight: 'auto', '.MuiTabs-indicator': { backgroundColor: 'secondary.main' } }}
              >
                {selectedWarehouses.map((warehouse) => (
                  <Tab 
                    key={warehouse.id}
                    label={ (
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        {warehouse.name}
                        <IconButton 
                          component="span"
                          size="small" 
                          onClick={(e) => onRemoveWarehouse(e, warehouse.id)}
                          sx={{ ml: 1, p: 0.2, color: 'inherit' }}
                          aria-label={`Depoyu kaldır: ${warehouse.name}`}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                    sx={{textTransform: 'none', fontSize: '0.8rem', minHeight: 'auto', pt:0.75, pb:0.75, borderRadius:1, mr:0.5, '&.Mui-selected': {backgroundColor: 'secondary.main', color: 'white'} }}
                    id={`warehouse-subtab-${warehouse.id}`}
                    aria-controls={`warehouse-subtabpanel-${warehouse.id}`}
                  />
                ))}
              </Tabs>
              {activeWarehouse && (
                <Box sx={{display: 'flex', alignItems: 'center', ml: 2}}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => onOpenCategoryModal(activeWarehouse.id)}
                    startIcon={<AddIcon fontSize='small' />}
                    sx={{ whiteSpace: 'nowrap', mr: 1}}
                  >
                    {t('warehouseStockSettingsTab.manageCategories')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={onOpenInventoryCategoryModal}
                    startIcon={<AddIcon fontSize='small' />}
                    sx={{ whiteSpace: 'nowrap'}}
                  >
                    {t('warehouseStockSettingsTab.createNewCategory')}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {selectedWarehouses.map((warehouse, warehouseIndex) => {
            // ✅ DEĞİŞİKLİK: Kategori sıralaması kaldırıldı - kullanıcı sıralayacak
            const categoriesForThisWarehouse = (selectedCategoriesPerWarehouse[warehouse.id] || [])
              .map(catId => allCategories.find(c => c.id === catId))
              .filter((c): c is InventoryCategory => !!c);
            
            const currentActiveCategoryTab = activeCategorySubTabPerWarehouse[warehouse.id] || 0;

            return (
              <div
                key={warehouse.id}
                role="tabpanel"
                hidden={activeWarehouseSubTab !== warehouseIndex}
                id={`warehouse-subtabpanel-${warehouse.id}`}
                aria-labelledby={`warehouse-subtab-${warehouse.id}`}
              >
                {activeWarehouseSubTab === warehouseIndex && (
                  <Box sx={{ p: 1.5, pt: 0 }}>
                    {categoriesForThisWarehouse.length > 0 ? (
                      <Box>
                        {isMobile ? (
                          // 📱 MOBİL: Kategori Select + Ürün Seç butonu
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel id={`category-select-label-${warehouse.id}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CategoryIcon fontSize="small" /> {t('warehouseStockSettingsTab.category')}
                                  </Box>
                                </InputLabel>
                                <Select
                                  labelId={`category-select-label-${warehouse.id}`}
                                  value={currentActiveCategoryTab}
                                  label={t('warehouseStockSettingsTab.category')}
                                  onChange={(e) => onActiveCategorySubTabChange(warehouse.id, e.target.value as number)}
                                >
                                  {categoriesForThisWarehouse.map((category, index) => (
                                    <MenuItem key={category.id} value={index}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography variant="body2">{category.name}</Typography>
                                        <IconButton 
                                          size="small" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveCategory(e, warehouse.id, category.id);
                                          }}
                                          sx={{ ml: 1, p: 0.2 }}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              {/* Ürün Seç Butonu */}
                              {categoriesForThisWarehouse[currentActiveCategoryTab] && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const activeCategoryForButton = categoriesForThisWarehouse[currentActiveCategoryTab];
                                    if (activeCategoryForButton) {
                                      onOpenProductModal(warehouse.id, activeCategoryForButton.id);
                                    }
                                  }}
                                  startIcon={<AddIcon fontSize="small" />}
                                  color="primary"
                                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                  {t('warehouseStockSettingsTab.productShort')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          // 💻 MASAÜSTÜ: Tab görünümü
                          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', mt: 1.5 }}>
                            <Tabs
                              value={currentActiveCategoryTab}
                              onChange={(e, newCatValue) => onActiveCategorySubTabChange(warehouse.id, newCatValue)}
                              variant="scrollable"
                              scrollButtons="auto"
                              allowScrollButtonsMobile
                                aria-label={t('warehouseStockSettingsTab.categoriesOfWarehouseAria', { warehouseName: warehouse.name })}
                              sx={{ 
                                flexGrow: 1, 
                                minHeight: 'auto', 
                                '.MuiTabs-indicator': { backgroundColor: 'secondary.main' } 
                              }}
                            >
                              {categoriesForThisWarehouse.map(category => (
                                <Tab
                                  key={category.id}
                                  label={(
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                      {category.name}
                                      <IconButton
                                        component="span"
                                        size="small"
                                        onClick={(e) => onRemoveCategory(e, warehouse.id, category.id)}
                                        sx={{
                                          ml: 1,
                                          p: 0.2,
                                          color: 'inherit',
                                        }}
                                        aria-label={t('warehouseStockSettingsTab.removeCategoryAria', { categoryName: category.name })}
                                      >
                                        <CloseIcon fontSize="inherit"/>
                                      </IconButton>
                                    </Box>
                                  )}
                                  id={`category-subtab-${warehouse.id}-${category.id}`}
                                  aria-controls={`category-subtabpanel-${warehouse.id}-${category.id}`}
                                />
                              ))}
                            </Tabs>
                            
                            {/* ✅ YENİ: Kategori Sıralama Butonları */}
                            {categoriesForThisWarehouse.length > 1 && (
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 0.5, 
                                borderLeft: 1, 
                                borderColor: 'divider',
                                pl: 1,
                                pr: 0.5
                              }}>
                                <Tooltip title={t('warehouseStockSettingsTab.moveCategoryLeft')}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const currentCategory = categoriesForThisWarehouse[currentActiveCategoryTab];
                                        if (currentCategory && onMoveCategory) {
                                          onMoveCategory(warehouse.id, currentCategory.id, 'left');
                                        }
                                      }}
                                      disabled={currentActiveCategoryTab === 0}
                                      color="primary"
                                      sx={{ p: 0.5 }}
                                    >
                                      <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t('warehouseStockSettingsTab.moveCategoryRight')}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const currentCategory = categoriesForThisWarehouse[currentActiveCategoryTab];
                                        if (currentCategory && onMoveCategory) {
                                          onMoveCategory(warehouse.id, currentCategory.id, 'right');
                                        }
                                      }}
                                      disabled={currentActiveCategoryTab === categoriesForThisWarehouse.length - 1}
                                      color="primary"
                                      sx={{ p: 0.5 }}
                                    >
                                      <ArrowForwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            )}
                            
                            {categoriesForThisWarehouse.length > 0 && categoriesForThisWarehouse[currentActiveCategoryTab] && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  const activeCategoryForButton = categoriesForThisWarehouse[currentActiveCategoryTab];
                                  if (activeCategoryForButton) {
                                    onOpenProductModal(warehouse.id, activeCategoryForButton.id);
                                  }
                                }}
                                startIcon={<AddIcon fontSize="small" />}
                                color="primary"
                                sx={{ ml: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                              >
                                {t('warehouseStockSettingsTab.selectProduct')}
                              </Button>
                            )}
                          </Box>
                        )}

                        {categoriesForThisWarehouse.map((category, categoryIndex) => {
                          const productsSelectedForThisCategoryIds = selectedProductsPerCategory[warehouse.id]?.[category.id] || [];
                          // ⚠️ Ürün detayları artık burada yüklenmiyor - sadece ID sayısını gösteriyoruz
                          const productCount = productsSelectedForThisCategoryIds.length;
                          const paginationKey = `${warehouse.id}_${category.id}`;
                          const currentPage = selectedProductsPage[paginationKey] || 0;
                          const currentRowsPerPage = selectedProductsRowsPerPage[paginationKey] || 5; // Default 5 rows per page

                          const paginatedProductIds = productsSelectedForThisCategoryIds.slice(
                            currentPage * currentRowsPerPage,
                            currentPage * currentRowsPerPage + currentRowsPerPage
                          );

                          return (
                            <div
                              key={category.id}
                              role="tabpanel"
                              hidden={currentActiveCategoryTab !== categoryIndex}
                              id={`category-subtabpanel-${warehouse.id}-${category.id}`}
                              aria-labelledby={`category-subtab-${warehouse.id}-${category.id}`}
                            >
                              {currentActiveCategoryTab === categoryIndex && (
                                <Box sx={{pt: 1.5}}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {productCount > 0
                                      ? t('warehouseStockSettingsTab.selectedProductCount', { count: productCount })
                                      : t('warehouseStockSettingsTab.noProductSelectedYet')
                                    }
                                  </Typography>
                                  {productCount > 0 && (
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                      {paginatedProductIds.map((productId) => {
                                        const product = selectedProductsDetails[productId];
                                        if (!product) {
                                          // ⚠️ Ürün detayı yüklenmediyse - SİLİNMİŞ veya BULUNAMAYAN ürün
                                          return (
                                            <Grid item xs={6} sm={4} md={2} key={productId}>
                                              <Card 
                                                variant="outlined" 
                                                sx={{ 
                                                  display: 'flex', 
                                                  flexDirection: 'column',
                                                  minHeight: 200,
                                                  position: 'relative',
                                                  borderColor: 'error.main',
                                                  borderWidth: 2,
                                                  bgcolor: 'error.50'
                                                }}
                                              >
                                                {/* SİL BUTONU - Sağ Üst Köşe */}
                                                {onRemoveProduct && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => onRemoveProduct(warehouse.id, category.id, productId)}
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 4,
                                                      right: 4,
                                                      bgcolor: 'error.main',
                                                      color: 'white',
                                                      zIndex: 1,
                                                      width: 28,
                                                      height: 28,
                                                      '&:hover': {
                                                        bgcolor: 'error.dark',
                                                      }
                                                    }}
                                                    aria-label={t('warehouseStockSettingsTab.removeDeletedProductAria')}
                                                  >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                )}
                                                
                                                {/* Uyarı İkonu */}
                                                <Box sx={{ 
                                                  height: 80, 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'center',
                                                  bgcolor: 'error.100'
                                                }}>
                                                  <DeleteIcon sx={{ fontSize: 40, color: 'error.main' }} />
                                                </Box>
                                                
                                                <CardContent sx={{ flex: 1, p: 1.5 }}>
                                                  <Typography 
                                                    variant="caption" 
                                                    fontWeight="bold" 
                                                    color="error.main"
                                                    sx={{ display: 'block', mb: 0.5 }}
                                                  >
                                                    {t('warehouseStockSettingsTab.deletedProduct')}
                                                  </Typography>
                                                  <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                      display: 'block',
                                                      fontSize: '0.65rem',
                                                      wordBreak: 'break-all'
                                                    }}
                                                  >
                                                    ID: {productId.slice(0, 8)}...
                                                  </Typography>
                                                  <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                      display: 'block',
                                                      fontSize: '0.6rem',
                                                      mt: 0.5
                                                    }}
                                                  >
                                                    {t('warehouseStockSettingsTab.deletedProductHint')}
                                                  </Typography>
                                                </CardContent>
                                              </Card>
                                            </Grid>
                                          );
                                        }
                                        
                                        // Thumbnail resmi (ilk resim)
                                        const thumbnailUrl = product.imageUrl?.split(',')[0] || null;
                                        
                                        return (
                                          <Grid item xs={6} sm={4} md={2} key={productId}>
                                            <Card 
                                              variant="outlined" 
                                              sx={{ 
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                position: 'relative', // ✅ Sil butonu için
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                  boxShadow: 3,
                                                  transform: 'translateY(-4px)'
                                                }
                                              }}
                                            >
                                              {/* ✅ SİL BUTONU - Sağ Üst Köşe */}
                                              {onRemoveProduct && (
                                                <IconButton
                                                  size="small"
                                                  onClick={() => onRemoveProduct(warehouse.id, category.id, productId)}
                                                  sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'error.main',
                                                    color: 'white',
                                                    zIndex: 1,
                                                    width: 28,
                                                    height: 28,
                                                    '&:hover': {
                                                      bgcolor: 'error.dark',
                                                    }
                                                  }}
                                                  aria-label={t('warehouseStockSettingsTab.removeProductAria', { productName: product.name })}
                                                >
                                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              )}
                                              
                                              {/* Ürün Resmi - Üstte */}
                                              {thumbnailUrl ? (
                                                <CardMedia
                                                  component="img"
                                                  sx={{ 
                                                    height: 120, 
                                                    objectFit: 'contain', 
                                                    p: 1.5,
                                                    bgcolor: 'grey.50'
                                                  }}
                                                  image={thumbnailUrl}
                                                  alt={product.name}
                                                />
                                              ) : (
                                                <Box sx={{ 
                                                  height: 120, 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'center',
                                                  bgcolor: 'grey.100'
                                                }}>
                                                  <ImageIcon sx={{ fontSize: 50, color: 'grey.400' }} />
                                                </Box>
                                              )}
                                              
                                              {/* Ürün Bilgileri - Altta */}
                                              <CardContent sx={{ 
                                                flex: 1,
                                                pt: 1.5,
                                                pb: 1.5,
                                                px: 1.5,
                                                '&:last-child': { pb: 1.5 }
                                              }}>
                                                {/* Ürün Adı */}
                                                <Typography 
                                                  variant="caption" 
                                                  fontWeight="bold"
                                                  sx={{ 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    lineHeight: 1.3,
                                                    mb: 1,
                                                    minHeight: '2.6em',
                                                    fontSize: '0.8rem'
                                                  }}
                                                >
                                                  {product.name}
                                                </Typography>
                                                
                                                {/* Fiyat ve Stok - Dikey */}
                                                <Box sx={{ 
                                                  display: 'flex', 
                                                  flexDirection: 'column',
                                                  gap: 0.5
                                                }}>
                                                  {product.salePrice !== undefined && (
                                                    <Chip 
                                                      label={`${product.salePrice.toFixed(2)} TL`}
                                                      size="small"
                                                      color="success"
                                                      sx={{ 
                                                        height: 22, 
                                                        fontSize: '0.7rem',
                                                        fontWeight: 'bold',
                                                        width: '100%'
                                                      }}
                                                    />
                                                  )}
                                                  {product.quantity !== undefined && (
                                                    <Chip 
                                                      label={`${product.quantity} ${product.unit || t('warehouseStockSettingsTab.piece')}`}
                                                      size="small"
                                                      color="info"
                                                      sx={{ 
                                                        height: 22, 
                                                        fontSize: '0.7rem',
                                                        width: '100%'
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </CardContent>
                                            </Card>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  )}
                                  {productCount > 5 && (
                                    <TablePagination
                                        component="div"
                                        count={productCount}
                                        page={currentPage}
                                        onPageChange={(event, newPage) => onSelectedProductsPageChange(paginationKey, newPage)}
                                        rowsPerPage={currentRowsPerPage}
                                        onRowsPerPageChange={(event) => onSelectedProductsRowsPerPageChange(paginationKey, event)}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        labelRowsPerPage={t('warehouseStockSettingsTab.pagination.rowsPerPageProducts')}
                                        labelDisplayedRows={({ from, to, count }) => t('warehouseStockSettingsTab.pagination.displayedRows', { from, to, count })}
                                        sx={{ mt: 0, borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                                      />
                                  )}
                                </Box>
                              )}
                            </div>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{mt:1.5}}>
                        {t('warehouseStockSettingsTab.noCategorySelectedHint')}
                      </Typography>
                    )}
                  </Box>
                )}
              </div>
            );
          })}
        </Box>
      )}

      {/* ✅ Taslak Kaydetme Dialog'u */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkAddIcon color="primary" />
          Taslak Olarak Kaydet
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Mevcut depo, kategori ve ürün seçimlerini isimli bir taslak olarak kaydedin.
            Aynı isimli taslak varsa üzerine yazılır. Taslaklar şirket genelindedir ve
            tüm hızlı satış / restoran ekranlarından kullanılabilir.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Taslak Adı"
            size="small"
            value={saveDialogName}
            onChange={(e) => { setSaveDialogName(e.target.value); setSaveDialogError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleConfirmSaveTemplate(); } }}
            error={Boolean(saveDialogError)}
            helperText={saveDialogError || 'Örn: "Mutfak Deposu" veya "Bar Menüsü"'}
            inputProps={{ maxLength: 64 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmSaveTemplate} variant="contained" disabled={!saveDialogName.trim()}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WarehouseStockSettingsTab; 